import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  makeStyles,
} from "@fluentui/react-components";
import { SettingsRegular } from "@fluentui/react-icons";
import { MdDragIndicator } from "react-icons/md";
import "./CSS/ColumnManager.css";

const useStyles = makeStyles({
  popoverSurface: {
    minWidth: "300px",
    maxHeight: "500px",
    overflowY: "auto",
  },
});

export default function ColumnManager({ columns, onColumnsChange }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newColumns = [...columns];
    const draggedColumn = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(index, 0, draggedColumn);

    onColumnsChange(newColumns);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleToggleColumn = (columnKey) => {
    const newColumns = columns.map((col) =>
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    );
    onColumnsChange(newColumns);
  };

  const handleResetColumns = () => {
    const resetColumns = columns.map((col) => ({ ...col, visible: true }));
    // Reset to default order
    const defaultOrder = [
      "audio",
      "direction",
      "recordingScope",
      "callerId",
      "duration",
      "agentName",
      "date",
      "transcription",
      "callId",
    ];
    const orderedColumns = defaultOrder
      .map((key) => resetColumns.find((col) => col.key === key))
      .filter(Boolean);
    onColumnsChange(orderedColumns);
  };

  return (
    <Popover>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          icon={<SettingsRegular />}
          appearance="subtle"
          title={t("COLUMN_SETTINGS")}
          className="floating-column-button"
        />
      </PopoverTrigger>
      <PopoverSurface className={styles.popoverSurface}>
        <div className="column-manager-container">
          <div className="column-manager-header">
            <h4>{t("MANAGE_COLUMNS")}</h4>
            <Button
              size="small"
              appearance="subtle"
              onClick={handleResetColumns}
            >
              {t("RESET")}
            </Button>
          </div>
          <div className="column-manager-list">
            {columns.map((column, index) => (
              <div
                key={column.key}
                className={`column-item ${draggedIndex === index ? "dragging" : ""}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <MdDragIndicator className="drag-icon" />
                <Checkbox
                  checked={column.visible}
                  onChange={() => handleToggleColumn(column.key)}
                  label={t(column.labelKey)}
                />
              </div>
            ))}
          </div>
          <div className="column-manager-footer">
            <small>
              {t("DRAG_TO_REORDER")}
            </small>
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
}
